import { html, imageUrl, raw } from './html';
import { icons } from './icons';
import { availableFormats } from './download';
import { initials } from './session';

/** The picture to show for a gallery pattern: a photo if it has one, otherwise its thumbnail. */
export function coverUrl(photos = []) {
  const photo = photos.find((item) => item && (item.urlMiddleSizePhoto || item.url));
  return photo ? imageUrl(photo.urlMiddleSizePhoto || photo.url) : '';
}

const POSSESSIVE = { mens: "men's", womens: "women's", kids: "kids'", girls: "girls'", boys: "boys'" };

/** 'womensTops' or 'womens-tops' becomes "women's tops", like the API's category names. */
export function categoryName(category = '') {
  const words = String(category).includes('-')
    ? String(category).split('-')
    : String(category).match(/[A-Za-z][a-z]*|[0-9]+/g) || [String(category)];
  const [first, ...rest] = words.map((word) => word.toLowerCase());
  return [POSSESSIVE[first] || first, ...rest].join(' ');
}

export function likedBy(likes, user) {
  return Boolean(user && likes && (likes.history || []).some((item) => item.id_user_action === user.id));
}

export function patternUrl(patternId, category) {
  const params = new URLSearchParams({ id: patternId });
  if (category && category !== 'all') params.set('category', category);
  return `/gallery/pattern/?${params}`;
}

export function authorBadge(userName = '') {
  return html`<span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue text-xs font-semibold text-white">${initials(userName)}</span>`;
}

export function likeButton({ liked, total, size = 'h-5 w-5' }) {
  return html`<button type="button" class="flex items-center gap-1 text-pink" data-like aria-pressed="${String(liked)}" aria-label="${liked ? 'Unlike' : 'Like'}">
    ${raw(liked ? icons.heartFilled({ size }) : icons.heart({ size }))}<span data-like-total>${total}</span>
  </button>`;
}

export function counter(icon, total, label) {
  return html`<span class="flex items-center gap-1 text-blue" title="${label}">${raw(icons[icon]())}<span>${total}</span></span>`;
}

/** The download icon with a little menu of paper sizes. The page script opens it and starts the download. */
export function downloadMenu(patternId, formats, total) {
  const sizes = availableFormats(formats);
  return html`<span class="relative" data-download-menu>
    <button type="button" class="flex items-center gap-1 text-blue hover:text-pink" data-download-toggle aria-haspopup="menu" aria-expanded="false" aria-label="Download PDF" title="Download PDF"${raw(sizes.length ? '' : ' disabled')}>
      ${raw(icons.download())}<span data-download-total>${total}</span>
    </button>
    <ul class="absolute right-0 bottom-full z-20 mb-2 hidden min-w-36 rounded-lg border border-line bg-white py-1 text-sm shadow-lg" role="menu" data-download-list>
      <li class="px-4 py-1 text-xs font-semibold text-muted uppercase" role="presentation">Paper size</li>
      ${raw(sizes.map((size) => html`<li role="none"><button type="button" role="menuitem" class="block w-full px-4 py-2 text-left hover:bg-sky hover:text-pink" data-download-format="${size}" data-pattern-id="${patternId}">${size}</button></li>`).join(''))}
    </ul>
  </span>`;
}

/** A gallery card. Every value from the API is escaped. */
export function patternCard(pattern, { user, category }) {
  const cover = coverUrl(pattern.photos);
  const likes = pattern.likes || { total: 0, history: [] };
  const liked = likedBy(likes, user);
  return html`<article class="mb-6 break-inside-avoid rounded-xl bg-white shadow-md ring-1 ring-black/5 transition hover:shadow-lg" data-pattern-id="${pattern._id}">
    <a href="${patternUrl(pattern._id, category)}" class="block overflow-hidden rounded-t-xl bg-sky">
      ${cover ? raw(html`<img src="${cover}" alt="${pattern.title}" loading="lazy" class="w-full" />`) : raw('<div class="aspect-[4/3]"></div>')}
    </a>
    <div class="space-y-3 p-4">
      <h2 class="text-lg font-semibold"><a href="${patternUrl(pattern._id, category)}" class="hover:text-pink">${pattern.title || 'Untitled pattern'}</a></h2>
      <div class="flex items-center justify-between gap-3 text-sm">
        <span class="flex min-w-0 items-center gap-2">${raw(authorBadge(pattern.user_name))}<span class="truncate">By ${pattern.user_name || 'a Patternade user'}</span></span>
        <span class="flex shrink-0 items-center gap-3">
          ${raw(likeButton({ liked, total: likes.total || 0 }))}
          ${raw(counter('eye', (pattern.views && pattern.views.total) || 0, 'Views'))}
          ${raw(downloadMenu(pattern._id, pattern.formats, (pattern.pdfDownloads && pattern.pdfDownloads.total) || 0))}
        </span>
      </div>
    </div>
  </article>`;
}

/** Flips a like button straight away; the caller sends the change to the API. */
export function flipLike(button) {
  const liked = button.getAttribute('aria-pressed') !== 'true';
  const totalEl = button.querySelector('[data-like-total]');
  const total = Math.max(0, Number(totalEl.textContent) + (liked ? 1 : -1));
  const size = button.querySelector('svg').getAttribute('class');
  button.outerHTML = likeButton({ liked, total, size });
}
